import { useModuleAccess } from "@/hooks/useModuleAccess";
import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AppLayout } from "@/components/AppLayout";
import {
  CalendarDays,
  Mail,
  ChevronRight,
  Clock,
  Users,
  FileText,
  ExternalLink,
  Plus,
  Cake,
  Star,
  CheckCircle,
  UserCheck,
  BookOpen,
  Shield,
  Globe,
  AlertTriangle,
  ShieldAlert,
  ClipboardList,
  BarChart3,
  LayoutGrid,
  HeartPulse,
  Stethoscope,
  MapPin,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { format, isToday, isTomorrow, addDays, startOfWeek, endOfWeek } from "date-fns";
import { useAuth } from "@/contexts/AuthContext";
import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useUiPref } from "@/hooks/useUiPref";
import { getLeaveStatusLabel, getLeaveTypeLabel } from "@/lib/leave-i18n";
import {
  apiGetEmployeeByEmail,
  apiGetLeaveRequests,
  apiGetMyLeaveBalance,
  apiGetMyTeam,
  apiGetJoinRequests,
  apiGetEmployees,
  apiCreateJoinRequest,
  apiApproveJoinRequest,
  apiRejectJoinRequest,
  apiGetCelebrations,
  apiSubmitConcern,
  apiGetConcernOpenCount,
  apiGetUiPrefs,
  apiGetMyWorkflowTasks,
  apiGetEmployeeSurveys,
  apiGetHRDocuments,
  apiGetCalendarEvents,
  apiGetCompanyDetails,
  apiGetMyOnboarding,
  getTenantSlug,
  API_BASE,
  type LeaveRequest,
  type CelebrationItem,
  type RaiseConcernOpenCount,
  type WorkflowTask,
  type Survey,
  type HRDocument,
  type QuickLink,
  type OnboardingSession,
  EXPIRY_DOCUMENT_TYPES,
  apiGetMedicalChecks,
  apiGetMedicalCheckAssignment,
  apiRespondToMedicalCheckAssignment,
  type MedicalCheckAssignment,
  apiGetTenantConfig,
  apiDownloadHRDocumentFile,
} from "@/lib/api";
import { ALWAYS_ON_MODULES } from "@/lib/modules";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { UserPlus, Crown, Check, X } from "lucide-react";
import { LeaveDetailModal } from "@/components/leave/LeaveDetailModal";
import { LeaveApprovalButtons } from "@/components/leave/LeaveApprovalButtons";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { EmployeeAvatar } from "@/components/EmployeeAvatar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FloatWrapper } from "@/components/ui/FormField";
import { useToast } from "@/hooks/use-toast";
import { getQuoteOfTheDay } from "@/lib/daily-quote";
import { formatLocalizedDate } from "@/lib/localized-date";
import { getMedicalCheckDetailDescriptionKey } from "@/lib/medical-check-display";
import {
  getEmployeeMedicalChecksForWeek,
  getEmployeeUpcomingMedicalChecks,
} from "@/lib/medical-check-dashboard";


// =========================================
// Helpers
// =========================================

function getGreetingKey(): string {
  const hour = new Date().getHours();
  if (hour < 12) return "dashboard.goodMorning";
  if (hour < 17) return "dashboard.goodAfternoon";
  return "dashboard.goodEvening";
}

// =========================================
// Inbox Highlights
// =========================================

function InboxHighlights() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { canAccess } = useModuleAccess();
  const employeeId = user?.employeeId ? Number(user.employeeId) : undefined;

  const { data: tasks = [] } = useQuery<WorkflowTask[]>({
    queryKey: ["myWorkflowTasks"],
    queryFn: apiGetMyWorkflowTasks,
    enabled: canAccess("myTasks"),
  });

  const { data: surveys = [] } = useQuery<Survey[]>({
    queryKey: ["employee-surveys"],
    queryFn: apiGetEmployeeSurveys,
    enabled: canAccess("mySurveys"),
  });

  const { data: docs = [] } = useQuery<HRDocument[]>({
    queryKey: ["hrDocuments", employeeId],
    queryFn: () => apiGetHRDocuments(employeeId),
    enabled: !!employeeId && canAccess("documents"),
  });

  const { data: onboarding } = useQuery<OnboardingSession | null>({
    queryKey: ["myOnboarding"],
    queryFn: apiGetMyOnboarding,
    enabled: canAccess("myOnboarding"),
  });

  const pendingTasks = (canAccess("myTasks") ? tasks : []).filter((t) => t.status === "pending" && !t.is_archived);
  const pendingSurveys = (canAccess("mySurveys") ? surveys : []).filter((s) => s.status === "active" && s.hasResponded === false);
  const pendingDocs = (canAccess("documents") ? docs : []).filter((d) => d.status === "Pending");
  const pendingOnboarding = (canAccess("myOnboarding") ? onboarding?.tasks ?? [] : []).filter(
    (t) => t.status === "pending" || t.status === "in_progress",
  );

  const total = pendingTasks.length + pendingSurveys.length + pendingDocs.length + pendingOnboarding.length;

  const items = [
    {
      count: pendingTasks.length,
      label: t("dashboard.workflowTasksPending", { count: pendingTasks.length }),
      icon: ClipboardList,
      route: "/my-tasks",
      color: "text-violet-500",
    },
    {
      count: pendingSurveys.length,
      label: t("dashboard.surveysPending", { count: pendingSurveys.length }),
      icon: BarChart3,
      route: "/my-surveys",
      color: "text-blue-500",
    },
    {
      count: pendingDocs.length,
      label: t("dashboard.documentsAwaitingSignature", { count: pendingDocs.length }),
      icon: FileText,
      route: pendingDocs.length === 1 ? `/my-documents?openDocument=${pendingDocs[0].id}` : "/my-documents",
      color: "text-amber-500",
    },
    {
      count: pendingOnboarding.length,
      label: t("dashboard.onboardingTasksRemaining", { count: pendingOnboarding.length }),
      icon: BookOpen,
      route: "/my-onboarding",
      color: "text-emerald-500",
    },
  ].filter((item) => item.count > 0);

  return (
    <Card className="shadow-card">
      <CardHeader className="pb-2 pt-4 px-5">
        <CardTitle className="text-base font-semibold text-foreground flex items-center gap-2">
          {t("dashboard.inboxHighlights")}
          {total > 0 && (
            <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold">
              {total > 9 ? "9+" : total}
            </span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="px-5 pb-4">
        {items.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t("dashboard.inboxUpToDate")}</p>
        ) : (
          <ul className="space-y-2">
            {items.map((item) => {
              const Icon = item.icon;
              return (
                <li key={item.route}>
                  <button
                    onClick={() => navigate(item.route)}
                    className="w-full flex items-center gap-3 rounded-md px-2 py-1.5 text-sm hover:bg-muted/60 transition-colors text-left"
                  >
                    <Icon className={`h-4 w-4 shrink-0 ${item.color}`} />
                    <span className="flex-1 text-foreground">{item.label}</span>
                    <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

// =========================================
// This Week Calendar
// =========================================

function ThisWeekCalendar({ employeeProfile }: { employeeProfile?: { id: string | number } | null }) {
  const { t } = useTranslation();
  const today = new Date();
  const weekStart = startOfWeek(today, { weekStartsOn: 1 });
  const weekEnd = endOfWeek(today, { weekStartsOn: 1 });
  const { data: rawEvents = [] } = useQuery({
    queryKey: ["calendar-events"],
    queryFn: apiGetCalendarEvents,
  });
  const { data: medicalCheckData } = useQuery({
    queryKey: ["medical-checks-dashboard"],
    queryFn: apiGetMedicalChecks,
    staleTime: 30_000,
  });

  const CATEGORY_COLORS: Record<string, string> = {
    meeting: "bg-blue-500",
    announcement: "bg-amber-500",
    celebration: "bg-pink-500",
    company: "bg-purple-500",
    bank_holiday: "bg-red-500",
    other: "bg-muted-foreground",
  };

  const corporateEvents = rawEvents
    .filter((e) => {
      const d = new Date(e.date);
      return d >= weekStart && d <= weekEnd;
    })
    .map((e) => ({
      id: e.id,
      title: e.title,
      date: new Date(e.date),
      type: e.category,
      color: CATEGORY_COLORS[e.category] ?? "bg-muted-foreground",
      source: "corporate" as const,
    }));
  const medicalEvents = getEmployeeMedicalChecksForWeek(
    medicalCheckData?.assignments,
    employeeProfile?.id,
    today,
  ).map(({ assignment, date }) => ({
      id: assignment.id,
      title: t("medicalCheckups.title"),
      date,
      type: `${assignment.institution}${assignment.scheduledTime ? ` · ${assignment.scheduledTime.slice(0, 5)}` : ""}`,
      color: "bg-rose-500",
      source: "medical" as const,
    }));
  const events = [...corporateEvents, ...medicalEvents]
    .sort((a, b) => a.date.getTime() - b.date.getTime());

  function formatEventDate(date: Date): string {
    if (isToday(date)) return t("dashboard.today");
    if (isTomorrow(date)) return t("dashboard.tomorrow");
    return format(date, "MMM d");
  }

  const navigate = useNavigate();

  return (
    <>
      <Card className="shadow-card">
        <CardHeader className="pb-2 pt-4 px-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <CardTitle className="text-base font-semibold text-foreground">
                {t("dashboard.thisWeek")}
              </CardTitle>
              <button
                onClick={() => navigate("/corporate-calendar")}
                className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors rounded px-1"
              >
                <span>{t("dashboard.allEvents")}</span>
                <ChevronRight className="h-3 w-3" />
              </button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="px-5 pb-4">
          {events.length === 0 ? (
            <div className="flex flex-col items-center py-6 text-center">
              <CalendarDays className="h-10 w-10 text-muted-foreground/40 mb-2" />
              <p className="text-sm text-muted-foreground">
                {t("dashboard.noUpcomingEvents")}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {events.map((event, i) => (
                <button
                  key={i}
                  className="w-full flex items-center gap-3 p-2 rounded-md hover:bg-muted text-left transition-colors"
                  onClick={() => navigate(event.source === "medical" ? `/?assignmentId=${event.id}` : `/corporate-calendar/${event.id}`)}
                >
                  <div className={`h-2 w-2 rounded-full shrink-0 ${event.color}`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground">{event.title}</p>
                    <p className="text-xs text-muted-foreground">{event.type}</p>
                  </div>
                  <span className="text-xs text-muted-foreground shrink-0">
                    {formatEventDate(event.date)}
                  </span>
                </button>
              ))}
            </div>
          )}
          <button
            onClick={() => navigate("/corporate-calendar")}
            className="mt-4 flex items-center gap-1 text-sm text-primary hover:text-primary/80 transition-colors font-medium"
          >
            {t("dashboard.openCalendar")} <ChevronRight className="h-4 w-4" />
          </button>
        </CardContent>
      </Card>

    </>
  );
}

// =========================================
// People to Celebrate
// =========================================

const CELEBRATE_LIMIT = 8;

function PeopleToCelebrate() {
  const { t } = useTranslation();
  const [dateFilter, setDateFilter] = useState<"7" | "30">("30");

  const { data: celebrations = [] } = useQuery<CelebrationItem[]>({
    queryKey: ["celebrations"],
    queryFn: apiGetCelebrations,
    staleTime: 5 * 60 * 1000,
  });

  // Only ever show within the selected window, capped at CELEBRATE_LIMIT total
  const within = celebrations
    .filter((c) => c.daysAway <= (dateFilter === "7" ? 7 : 30))
    .slice(0, CELEBRATE_LIMIT);

  const nextSevenDays  = within.filter((c) => c.daysAway <= 7);
  const nextThirtyDays = within.filter((c) => c.daysAway > 7 && c.daysAway <= 30);

  function formatDaysAway(days: number): string {
    if (days === 0) return t("dashboard.today");
    if (days === 1) return t("dashboard.tomorrow");
    return t("dashboard.inDays", { count: days });
  }

  function CelebrationCard({ person }: { person: CelebrationItem }) {
    const label = person.eventType === "birthday"
      ? t("dashboard.celebrateBirthday", { name: person.name })
      : t("dashboard.celebrateAnniversary", { name: person.name, years: person.years });
    return (
      <div className="flex items-center gap-3 py-2">
        <div className="relative shrink-0">
          <EmployeeAvatar
            avatarUrl={person.avatarUrl}
            avatarColor={person.avatarColor}
            initials={person.initials}
            size="sm"
            className="h-9 w-9 text-xs"
          />
          <div className="absolute -bottom-0.5 -right-0.5 h-4 w-4 rounded-full bg-card border border-border flex items-center justify-center">
            {person.eventType === "birthday" ? (
              <Cake className="h-2.5 w-2.5 text-pink-500" />
            ) : (
              <Star className="h-2.5 w-2.5 text-amber-500" />
            )}
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-foreground leading-tight">
            {label}
          </p>
          <p className="text-xs text-muted-foreground">
            {formatDaysAway(person.daysAway)}
          </p>
        </div>
        <a
          href={`mailto:?subject=Happy%20${person.eventType === "birthday" ? "Birthday" : "Work%20Anniversary"}%20${encodeURIComponent(person.name)}&body=Hi%20${encodeURIComponent(person.name)}%2C%0A%0AWishing%20you%20a%20wonderful%20${person.eventType === "birthday" ? "birthday" : "work%20anniversary"}!`}
          className="shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-border text-xs font-medium text-foreground hover:bg-muted transition-colors"
        >
          <Mail className="h-3 w-3" />
          {t("common.email")}
        </a>
      </div>
    );
  }

  return (
    <Card className="shadow-card">
      <CardHeader className="pb-2 pt-4 px-5">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base font-semibold text-foreground">
            {t("dashboard.peopleToCelebrate")}
          </CardTitle>
          <select
            className="bg-transparent border-none focus:ring-0 text-xs text-muted-foreground hover:text-foreground outline-none cursor-pointer"
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value as "7" | "30")}
          >
            <option value="7">{t("dashboard.next7Days", "Next 7 days")}</option>
            <option value="30">{t("dashboard.next30Days")}</option>
          </select>
        </div>
      </CardHeader>
      <CardContent className="px-5 pb-4">
        {nextSevenDays.length > 0 && (
          <div>
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1 mt-2">
              {t("dashboard.next7Days", "Next 7 days")}
            </p>
            <div className="divide-y divide-border/50">
              {nextSevenDays.map((person, i) => (
                <CelebrationCard key={i} person={person} />
              ))}
            </div>
          </div>
        )}
        {nextThirtyDays.length > 0 && (
          <div className={nextSevenDays.length > 0 ? "mt-3" : ""}>
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1 mt-2">
              {t("dashboard.next30Days")}
            </p>
            <div className="divide-y divide-border/50">
              {nextThirtyDays.map((person, i) => (
                <CelebrationCard key={i} person={person} />
              ))}
            </div>
          </div>
        )}
        {within.length === 0 && (
          <p className="text-sm text-muted-foreground py-4 text-center">
            {dateFilter === "7"
              ? t("dashboard.noCelebrationsNext7", "No upcoming celebrations in the next 7 days.")
              : t("dashboard.noCelebrationsNext30", "No upcoming celebrations in the next 30 days.")}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

// =========================================
// Profile Card (Right Sidebar)
// =========================================

function ProfileCard({ employee }: { employee: any }) {
  const navigate = useNavigate();
  const color = employee?.avatarColor || "from-purple-400 to-indigo-500";
  const isHex = color.startsWith("#");

  return (
    <div
      className="relative cursor-pointer group"
      onClick={() => navigate(`/employees/${employee?.id}`)}
    >
      {/* Badge card */}
      <div className="relative z-10 w-full rounded-2xl border border-border bg-card shadow-card overflow-hidden group-hover:shadow-elevated transition-shadow">
        {/* Rigid top bar with eyelet hole */}
        <div
          className={`relative h-10 w-full flex items-center justify-center ${!isHex ? `bg-gradient-to-r ${color}` : ""}`}
          style={isHex ? { backgroundColor: color } : undefined}
        >
          {/* Eyelet hole */}
          <div className="w-5 h-2.5 rounded-full bg-black/25 border border-black/10" />
        </div>

        {/* Avatar — below the bar, no overlap */}
        <div className="relative z-10 flex flex-col items-center -mt-5 px-4 pb-4">
          <EmployeeAvatar
            avatarUrl={employee?.avatarUrl}
            avatarColor={color}
            initials={employee?.initials || "?"}
            size="xl"
            className="ring-4 ring-card shadow-md"
          />
          <div className="mt-3 text-center w-full">
            <p className="font-semibold text-foreground text-base leading-tight truncate">
              {employee?.name}
            </p>
            <p className="text-sm text-muted-foreground mt-0.5 truncate">
              {employee?.jobTitle}
            </p>
            {employee?.department && (
              <p className="text-xs text-muted-foreground/60 mt-0.5 truncate">
                {employee?.department}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// =========================================
// Time Off Widget (Right Sidebar)
// =========================================

function TimeOffWidget({
  employeeId,
  leaveRequests,
}: {
  employeeId: string;
  leaveRequests: LeaveRequest[];
}) {
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();
  const { data: balances = [] } = useQuery({
    queryKey: ["myLeaveBalance", currentYear],
    queryFn: () => apiGetMyLeaveBalance(currentYear),
    enabled: !!employeeId,
  });

  const [detailRequest, setDetailRequest] = useState<LeaveRequest | null>(null);

  const totalRemainingDays = balances
    .filter((b) => b.leaveType === "annual")
    .reduce((sum, b) => sum + (b.availableNow ?? 0), 0);

  // Upcoming approved leaves
  const upcomingLeaves = leaveRequests
    .filter(
      (r) =>
        r.status === "Approved" && new Date(r.startDate) >= new Date()
    )
    .slice(0, 4);

  const leaveStatusColor: Record<string, string> = {
    Approved: "bg-green-500",
    Pending: "bg-yellow-500",
    Rejected: "bg-red-500",
  };

  function formatLeaveDate(req: LeaveRequest): string {
    const start = format(new Date(req.startDate), "d MMM");
    const end = format(new Date(req.endDate), "d MMM");
    if (start === end) return start;
    return `${start} – ${end}`;
  }

  const navigate = useNavigate();

  return (
    <>
      <Card className="shadow-card">
        <CardHeader className="pb-2 pt-4 px-4">
          <CardTitle className="text-base font-semibold text-foreground">
            {t("dashboard.availableDaysLeave", { defaultValue: "Available days for leave" })}
          </CardTitle>
        </CardHeader>
        <CardContent className="px-4 pb-4">
          <p className="text-3xl font-bold text-foreground mb-3">
            {totalRemainingDays > 0
              ? t("dashboard.daysCount", { count: totalRemainingDays })
              : "0d"}
          </p>

          {/* Upcoming leaves */}
          <div className="space-y-2 mb-3">
            {upcomingLeaves.map((req) => (
              <button
                key={req.id}
                onClick={() => setDetailRequest(req)}
                className="w-full text-left"
              >
                <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-muted/50 hover:bg-muted transition-colors">
                  <div
                    className={`h-2 w-2 rounded-full shrink-0 ${leaveStatusColor[req.status] ?? "bg-muted-foreground"}`}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-foreground truncate">
                      {getLeaveTypeLabel(t, req.leaveType, req.leaveTypeLabel)}
                    </p>
                    <p className="text-xs text-muted-foreground capitalize">
                      {getLeaveStatusLabel(t, req.status)}
                    </p>
                  </div>
                  <span className="text-xs text-muted-foreground shrink-0">
                    {formatLeaveDate(req)}
                  </span>
                </div>
              </button>
            ))}
          </div>

          <div className="flex items-center justify-between pt-2 border-t border-border">
            <button
              onClick={() => navigate("/leave")}
              className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              <span>{t("dashboard.moreCount", { count: upcomingLeaves.length })}</span>
              <ChevronRight className="h-3.5 w-3.5" />
            </button>
            <button
              onClick={() => navigate("/leave?newRequest=1")}
              className="flex items-center gap-1 text-xs text-primary hover:text-primary/80 transition-colors font-medium"
            >
              <Plus className="h-3.5 w-3.5" />
              {t("dashboard.requestTimeOff")}
            </button>
          </div>
        </CardContent>
      </Card>

      <LeaveDetailModal
        request={detailRequest}
        open={!!detailRequest}
        onOpenChange={(v) => {
          if (!v) setDetailRequest(null);
        }}
      />
    </>
  );
}

// =========================================
// Team Widget (Right Sidebar)
// =========================================

function TeamWidget({ selfServiceOnly = false }: { selfServiceOnly?: boolean }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showRequestDialog, setShowRequestDialog] = useState(false);
  const [requesting, setRequesting] = useState(false);
  const [selectedTargetId, setSelectedTargetId] = useState("");

  const { data: team, isLoading } = useQuery({
    queryKey: ["my-team"],
    queryFn: apiGetMyTeam,
  });

  const { data: joinRequests = [] } = useQuery({
    queryKey: ["join-requests"],
    queryFn: apiGetJoinRequests,
    enabled: !selfServiceOnly,
  });

  const { data: allEmployees = [] } = useQuery({
    queryKey: ["employees"],
    queryFn: apiGetEmployees,
    enabled: showRequestDialog && !selfServiceOnly,
  });

  // Employees not already on this team (candidates to request)
  const myTeamIds = new Set((team?.members ?? []).map((m) => m.id));
  const requestCandidates = allEmployees.filter(
    (e: any) => !myTeamIds.has(String(e.id))
  );

  async function handleSendRequest() {
    if (!selectedTargetId) return;
    setRequesting(true);
    try {
      await apiCreateJoinRequest(selectedTargetId);
      queryClient.invalidateQueries({ queryKey: ["join-requests"] });
      setShowRequestDialog(false);
      setSelectedTargetId("");
    } finally {
      setRequesting(false);
    }
  }

  async function handleApprove(id: number) {
    await apiApproveJoinRequest(id);
    queryClient.invalidateQueries({ queryKey: ["join-requests"] });
    queryClient.invalidateQueries({ queryKey: ["my-team"] });
  }

  async function handleReject(id: number) {
    await apiRejectJoinRequest(id);
    queryClient.invalidateQueries({ queryKey: ["join-requests"] });
  }

  if (isLoading) return null;
  if (!team || team.members.length === 0) return null;

  const teamLabel = team.teamName ?? t("dashboard.team", "Team");

  return (
    <>
      <Card className="shadow-card">
        <CardHeader className="pb-2 pt-4 px-4">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base font-semibold text-foreground">
              {teamLabel}
            </CardTitle>
            {team.isLeader && !selfServiceOnly && (
              <button
                onClick={() => setShowRequestDialog(true)}
                className="flex items-center gap-1 text-xs text-primary hover:underline"
              >
                <UserPlus className="h-3.5 w-3.5" />
                {t("dashboard.teamWidget.requestMember", "Request member")}
              </button>
            )}
          </div>
        </CardHeader>
        <CardContent className="px-4 pb-4 space-y-2">
          {team.members.map((member) => {
            const avatarUrl = member.avatarUrl
              ? member.avatarUrl.startsWith("http")
                ? member.avatarUrl
                : `${API_BASE}${member.avatarUrl}`
              : null;
            return (
              <button
                key={member.id}
                onClick={() => navigate(`/employees/${member.id}`)}
                className="w-full flex items-center gap-3 rounded-lg hover:bg-muted/60 transition-colors p-1.5 -mx-1.5 text-left group"
              >
                <EmployeeAvatar
                  avatarUrl={avatarUrl}
                  avatarColor={member.avatarColor || "from-blue-400 to-indigo-500"}
                  initials={member.initials}
                  size="sm"
                  className="h-8 w-8 shrink-0 text-xs"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <p className="text-sm font-medium text-foreground truncate">
                      {member.name}
                    </p>
                    {member.isLeader && (
                      <Crown className="h-3 w-3 text-amber-500 shrink-0" />
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground truncate">{member.jobTitle}</p>
                </div>
                <ChevronRight className="h-3.5 w-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 shrink-0" />
              </button>
            );
          })}
        </CardContent>
      </Card>

      {/* Pending join requests for this LM to review */}
      {!selfServiceOnly && joinRequests.length > 0 && (
        <Card className="shadow-card">
          <CardHeader className="pb-2 pt-4 px-4">
            <CardTitle className="text-base font-semibold text-foreground">
              {t("dashboard.teamWidget.pendingRequests", "Pending Team Requests")}
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4 space-y-3">
            {joinRequests.map((req) => (
              <div key={req.id} className="rounded-lg border p-3 space-y-2">
                <p className="text-xs text-muted-foreground">
                  <span className="font-medium text-foreground">{req.requester.name}</span>
                  {t("dashboard.teamWidget.wants", " wants ")}
                  <span className="font-medium text-foreground">{req.target.name}</span>
                  {t("dashboard.teamWidget.toJoinTheirTeam", " to join their team")}
                </p>
                <div className="flex gap-2">
                  <Button size="sm" variant="default" className="h-7 text-xs gap-1" onClick={() => handleApprove(req.id)}>
                    <Check className="h-3 w-3" /> {t("teamsManagement.approve", "Approve")}
                  </Button>
                  <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={() => handleReject(req.id)}>
                    <X className="h-3 w-3" /> {t("teamsManagement.reject", "Reject")}
                  </Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Request member dialog */}
      <Dialog open={!selfServiceOnly && showRequestDialog} onOpenChange={setShowRequestDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{t("dashboard.teamWidget.requestDialogTitle", "Request Team Member")}</DialogTitle>
            <DialogDescription>
              {t("dashboard.teamWidget.requestDialogDesc", "Select an employee to request joining your team. Their current line manager will need to approve.")}
            </DialogDescription>
          </DialogHeader>
          <div className="pb-2">
            <FloatWrapper label={t("payroll.employee", "Employee")}>
              <select
                className="w-full rounded-xl border border-[#d0d5e8] h-14 px-4 text-sm appearance-none"
                value={selectedTargetId}
                onChange={(e) => setSelectedTargetId(e.target.value)}
              >
                <option value="">{t("dashboard.teamWidget.selectEmployee", "Select employee...")}</option>
                {requestCandidates.map((e: any) => (
                  <option key={e.id} value={String(e.id)}>
                    {e.name}{e.jobTitle ? ` — ${e.jobTitle}` : ""}
                  </option>
                ))}
              </select>
            </FloatWrapper>
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" size="sm" onClick={() => setShowRequestDialog(false)}>
              {t("common.cancel", "Cancel")}
            </Button>
            <Button size="sm" disabled={!selectedTargetId || requesting} onClick={handleSendRequest}>
              {t("dashboard.teamWidget.sendRequest", "Send Request")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}



// =========================================
// Shortcuts Widget (Right Sidebar)
// =========================================

// Icon and colour lookups for DB-driven quick links
const QL_ICON_MAP: Record<string, React.ComponentType<{ className?: string }>> = {
  BookOpen, Clock, FileText, Shield, Globe, UserCheck,
  Users, BarChart3, Star, ClipboardList, AlertTriangle, ShieldAlert,
  Calendar: CalendarDays, Briefcase: CheckCircle, Home: LayoutGrid, Mail,
  Award: CheckCircle, Zap: AlertTriangle, Link2: ExternalLink,
};

const QL_COLOR_MAP: Record<string, string> = {
  blue: "bg-blue-500",
  green: "bg-green-500",
  orange: "bg-orange-500",
  purple: "bg-purple-500",
  red: "bg-red-500",
  cyan: "bg-cyan-500",
  amber: "bg-amber-500",
  indigo: "bg-indigo-500",
};

const DEFAULT_QUICK_LINKS: QuickLink[] = [
  { id: "d1", label: "Employee Handbook", linkType: "url", href: "#", icon: "BookOpen", color: "orange" },
  { id: "d2", label: "Onboarding Page", linkType: "internal", href: "/onboarding", icon: "UserCheck", color: "blue" },
  { id: "d3", label: "Time Off & Attendance", linkType: "internal", href: "/leave", icon: "Clock", color: "green" },
  { id: "d4", label: "Payroll & Benefits", linkType: "internal", href: "/my-salary", icon: "FileText", color: "purple" },
  { id: "d5", label: "IT Security", linkType: "url", href: "#", icon: "Shield", color: "red" },
  { id: "d6", label: "Company Intranet", linkType: "url", href: "#", icon: "Globe", color: "cyan" },
];

function ShortcutsWidget() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [open, setOpen] = useUiPref("widget_quicklinks_open", true);
  const toggle = () => { if (open !== undefined) setOpen(!open); };

  const { data: company } = useQuery({
    queryKey: ["companyDetails", getTenantSlug()],
    queryFn: apiGetCompanyDetails,
    staleTime: 5 * 60_000,
  });

  const shortcuts = company?.quickLinks?.length ? company.quickLinks : DEFAULT_QUICK_LINKS;

  function renderLink(shortcut: QuickLink, i: number) {
    const IconComp = QL_ICON_MAP[shortcut.icon] ?? ExternalLink;
    const colorCls = QL_COLOR_MAP[shortcut.color] ?? "bg-blue-500";
    const isInternal = shortcut.linkType === "internal";
    const isExternal = shortcut.linkType === "url";
    const isFile = shortcut.linkType === "file";

    const inner = (
      <>
        <div className={`h-7 w-7 rounded-md ${colorCls} flex items-center justify-center shrink-0`}>
          <IconComp className="h-4 w-4 text-white" />
        </div>
        <span className="flex-1 text-left text-sm text-foreground group-hover:text-foreground transition-colors truncate">
          {shortcut.id.startsWith("d") ? t(`dashboard.quickLinkDefaults.${shortcut.id}`, shortcut.label) : shortcut.label}
        </span>
        <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
      </>
    );

    if (isInternal) {
      return (
        <button
          key={i}
          onClick={() => navigate(shortcut.href)}
          className="w-full flex items-center gap-3 py-2 px-2 rounded-lg hover:bg-muted transition-colors group"
        >
          {inner}
        </button>
      );
    }

    return (
      <a
        key={i}
        href={shortcut.href === "#" ? undefined : shortcut.href}
        target={isExternal || isFile ? "_blank" : undefined}
        rel={isExternal || isFile ? "noopener noreferrer" : undefined}
        className={`flex items-center gap-3 py-2 px-2 rounded-lg hover:bg-muted transition-colors group${shortcut.href === "#" ? " cursor-default" : ""}`}
      >
        {inner}
      </a>
    );
  }

  return (
    <Card className="shadow-card">
      <CardHeader className="py-4 px-4">
        <button
          onClick={toggle}
          disabled={open === undefined}
          className="w-full flex items-center justify-between gap-2"
        >
          <span className="text-base font-semibold text-foreground">
            {t("dashboard.quickLinks")}
          </span>
          <ChevronRight
            className={`h-4 w-4 text-muted-foreground shrink-0 transition-transform duration-200 ${open === true ? "rotate-90" : ""}`}
          />
        </button>
      </CardHeader>
      <div
        className={`grid transition-all duration-300 ease-in-out ${open === true ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}
      >
        <div className="overflow-hidden">
          <CardContent className="px-4 pb-4 space-y-1">
            {shortcuts.map((shortcut, i) => renderLink(shortcut, i))}
          </CardContent>
        </div>
      </div>
    </Card>
  );
}

// =========================================
// Open Cases Widget (HR only, Right Sidebar)
// =========================================

function OpenCasesWidget() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [open, setOpen] = useUiPref("widget_opencases_open", true);
  const toggle = () => { if (open !== undefined) setOpen(!open); };

  const { data: counts } = useQuery<RaiseConcernOpenCount>({
    queryKey: ["concern-open-count", getTenantSlug()],
    queryFn: apiGetConcernOpenCount,
    staleTime: 60_000,
  });

  if (!counts || counts.openCount === 0) return null;

  return (
    <Card className="shadow-card border-orange-200 dark:border-orange-800">
      <CardHeader className="py-4 px-4">
        <button
          onClick={toggle}
          disabled={open === undefined}
          className="w-full flex items-center justify-between gap-2"
        >
          <span className="text-base font-semibold text-foreground flex items-center gap-2">
            <ShieldAlert className="h-4 w-4 text-orange-500" />
            {t("raiseConcern.openCasesWidget")}
            <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-orange-500 text-white text-[10px] font-bold">
              {counts.openCount > 9 ? "9+" : counts.openCount}
            </span>
          </span>
          <ChevronRight
            className={`h-4 w-4 text-muted-foreground shrink-0 transition-transform duration-200 ${open === true ? "rotate-90" : ""}`}
          />
        </button>
      </CardHeader>
      <div className={`grid transition-all duration-300 ease-in-out ${open === true ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}>
        <div className="overflow-hidden">
          <CardContent className="px-4 pb-4 space-y-2">
            <div className="grid grid-cols-3 gap-2">
              {[
                { label: t("raiseConcern.newCases"), value: counts.newCount, color: "text-blue-600", filter: "submitted" },
                { label: t("raiseConcern.inReviewCases"), value: counts.inReviewCount, color: "text-yellow-600", filter: "in_review" },
                { label: t("raiseConcern.assignedCases"), value: counts.assignedCount, color: "text-orange-600", filter: "assigned" },
              ].map((s) => (
                <button
                  key={s.label}
                  onClick={() => navigate(`/hr-cases?filter=${s.filter}`)}
                  className="text-center p-2 rounded-lg bg-muted/50 hover:bg-muted transition-colors cursor-pointer"
                >
                  <p className={`text-lg font-bold ${s.color}`}>{s.value}</p>
                  <p className="text-[10px] text-muted-foreground leading-tight">{s.label}</p>
                </button>
              ))}
            </div>
            <button
              id="open-cases-view-all-btn"
              onClick={() => navigate("/hr-cases")}
              className="w-full flex items-center justify-center gap-1 text-sm text-orange-600 hover:text-orange-700 font-medium mt-1 transition-colors"
            >
              {t("raiseConcern.viewAllCases")} <ChevronRight className="h-4 w-4" />
            </button>
          </CardContent>
        </div>
      </div>
    </Card>
  );
}

function PendingApprovalsWidget() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [detailRequest, setDetailRequest] = useState<LeaveRequest | null>(null);

  const { data: pendingRequests = [], isLoading } = useQuery({
    queryKey: ["leaveRequests", { status: "Pending" }],
    queryFn: () => apiGetLeaveRequests({ status: "Pending" }),
  });

  const displayRequests = pendingRequests.slice(0, 5);

  return (
    <>
      <Card className="shadow-card">
        <CardHeader className="pb-2 pt-4 px-5">
          <CardTitle className="text-base font-semibold flex items-center gap-2 text-foreground">
            <UserCheck className="h-4 w-4 text-primary" />
            {t("dashboard.pendingApprovalsSection")}
            {pendingRequests.length > 0 && (
              <span className="ml-1 inline-flex h-5 w-5 items-center justify-center rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold">
                {pendingRequests.length > 9 ? "9+" : pendingRequests.length}
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="px-5 pb-4 space-y-1">
          {isLoading ? (
            [1, 2, 3].map((i) => (
              <div key={i} className="flex items-center gap-3 p-1.5 animate-pulse">
                <div className="h-9 w-9 rounded-full bg-muted shrink-0" />
                <div className="flex-1 space-y-1.5">
                  <div className="h-3 bg-muted rounded w-24" />
                  <div className="h-3 bg-muted rounded w-36" />
                </div>
                <div className="h-7 bg-muted rounded w-36 shrink-0" />
              </div>
            ))
          ) : displayRequests.length === 0 ? (
            <div className="flex items-center gap-2.5 py-3 text-muted-foreground">
              <CheckCircle className="h-5 w-5 text-green-500 shrink-0" />
              <p className="text-sm">{t("dashboard.allCaughtUp")}</p>
            </div>
          ) : (
            displayRequests.map((request) => {
              const emp = request.employeeName
                .split(" ")
                .map((n: string) => n[0])
                .join("")
                .slice(0, 2)
                .toUpperCase();
              const isOwnRequest =
                !!user?.employeeId &&
                String(request.employeeId) === String(user.employeeId);
              return (
                <div
                  key={request.id}
                  className="flex items-center gap-2 rounded-lg hover:bg-muted/30 transition-colors"
                >
                  <button
                    className="flex items-center gap-3 text-left flex-1 min-w-0 p-1.5"
                    onClick={() => setDetailRequest(request)}
                  >
                    <div className="h-9 w-9 rounded-full gradient-brand flex items-center justify-center text-primary-foreground text-xs font-semibold shrink-0">
                      {emp}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground">
                        {request.employeeName}
                      </p>
                      <p className="text-xs text-muted-foreground truncate">
                        {request.leaveTypeLabel ?? request.leaveType}
                        {" · "}
                        {format(new Date(request.startDate), "d MMM")}
                        {request.startDate !== request.endDate &&
                          ` – ${format(new Date(request.endDate), "d MMM")}`}
                        {" · "}
                        {request.workingDays}d
                      </p>
                    </div>
                  </button>
                  <div className="shrink-0 pr-1.5">
                    {isOwnRequest ? (
                      <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400">
                        <span className="h-1.5 w-1.5 rounded-full bg-yellow-500" />
                        {t("leave.awaitingApproval")}
                      </span>
                    ) : (
                      <LeaveApprovalButtons requestId={request.id} />
                    )}
                  </div>
                </div>
              );
            })
          )}
        </CardContent>
      </Card>
      <LeaveDetailModal
        request={detailRequest}
        open={!!detailRequest}
        onOpenChange={(v) => {
          if (!v) setDetailRequest(null);
        }}
      />
    </>
  );
}

// =========================================
// Medical Checks Widget
// =========================================

function MedicalChecksWidget({
  employeeProfile,
  showCard = true,
}: {
  employeeProfile?: { id: string | number } | null;
  showCard?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const qc = useQueryClient();
  const { toast } = useToast();

  const [detailCheck, setDetailCheck] = useState<MedicalCheckAssignment | null>(null);
  const [rejectingCheck, setRejectingCheck] = useState<MedicalCheckAssignment | null>(null);
  const [rejectionReason, setRejectionReason] = useState("");
  const [preferredSlot, setPreferredSlot] = useState("");

  const { data: tenantConfig } = useQuery({
    queryKey: ["tenant-config"],
    queryFn: apiGetTenantConfig,
    staleTime: 10 * 60 * 1000,
  });
  const medicalModuleOn = (() => {
    const modules = tenantConfig?.enabledModules;
    if (!modules) return true;
    return ALWAYS_ON_MODULES.has("medicalCheckups") || modules["medicalCheckups"] !== false;
  })();

  const { data: medicalCheckData } = useQuery({
    queryKey: ["medical-checks-dashboard"],
    queryFn: apiGetMedicalChecks,
    staleTime: 30_000,
    enabled: medicalModuleOn,
  });

  const myVisibleChecks = getEmployeeUpcomingMedicalChecks(
    medicalCheckData?.assignments,
    employeeProfile?.id,
  );
  const pendingCount = myVisibleChecks.filter((check) => check.status === "pending").length;

  const deepLinkAssignmentId = searchParams.get("assignmentId");
  const numericDeepLinkId = deepLinkAssignmentId ? Number(deepLinkAssignmentId) : null;
  const { data: deepLinkAssignment, error: deepLinkError, isLoading: deepLinkLoading } = useQuery({
    queryKey: ["medical-check-assignment", numericDeepLinkId],
    queryFn: () => apiGetMedicalCheckAssignment(numericDeepLinkId!),
    enabled: Number.isInteger(numericDeepLinkId) && Number(numericDeepLinkId) > 0,
    retry: false,
  });
  useEffect(() => {
    if (deepLinkAssignment) setDetailCheck(deepLinkAssignment);
  }, [deepLinkAssignment]);

  const handleAccept = async (checkId: number) => {
    try {
      const res = await apiRespondToMedicalCheckAssignment(checkId, { action: "accept" });
      if (res.success) {
        toast({
          title: t("common.success", "Success"),
          description: t("medicalCheckups.messages.accepted"),
        });
        qc.invalidateQueries({ queryKey: ["medical-checks-dashboard"] });
        qc.invalidateQueries({ queryKey: ["notifications"] });
        qc.invalidateQueries({ queryKey: ["notificationCount"] });
        setDetailCheck(null);
      }
    } catch (err: any) {
      toast({
        title: t("common.error", "Error"),
        description: t("medicalCheckups.messages.failed"),
        variant: "destructive",
      });
    }
  };

  const handleReject = async () => {
    if (!rejectingCheck) return;
    if (!rejectionReason.trim()) {
      toast({
        title: t("common.error", "Error"),
        description: t("medicalCheckups.rejectDialog.reasonRequired"),
        variant: "destructive",
      });
      return;
    }

    try {
      const combinedReason = preferredSlot.trim()
        ? `${rejectionReason.trim()}\n\n[Preferred slot: ${preferredSlot.trim()}]`
        : rejectionReason.trim();

      const res = await apiRespondToMedicalCheckAssignment(rejectingCheck.id, {
        action: "reject",
        rejectionReason: combinedReason,
      });

      if (res.success) {
        toast({
          title: t("common.success", "Success"),
          description: t("medicalCheckups.messages.rejected"),
        });
        qc.invalidateQueries({ queryKey: ["medical-checks-dashboard"] });
        qc.invalidateQueries({ queryKey: ["notifications"] });
        qc.invalidateQueries({ queryKey: ["notificationCount"] });
        setRejectingCheck(null);
        setDetailCheck(null);
        setRejectionReason("");
        setPreferredSlot("");
      }
    } catch (err: any) {
      toast({
        title: t("common.error", "Error"),
        description: t("medicalCheckups.messages.failed"),
        variant: "destructive",
      });
    }
  };

  if (myVisibleChecks.length === 0 && !deepLinkAssignmentId && !detailCheck) return null;

  return (
    <>
      {showCard && myVisibleChecks.length > 0 && <Card className="shadow-card border-rose-200 dark:border-rose-900/50 bg-gradient-to-r from-rose-500/5 to-transparent">
        <CardHeader className="pb-2 pt-4 px-5">
          <CardTitle className="text-base font-semibold flex items-center gap-2 text-foreground">
            <div className="h-7 w-7 rounded-full bg-rose-500/10 dark:bg-rose-500/20 text-rose-600 dark:text-rose-400 flex items-center justify-center shrink-0">
              <HeartPulse className="h-4.5 w-4.5 animate-pulse" />
            </div>
            {t("medicalCheckups.title")}
            {pendingCount > 0 && <span className="ml-1 inline-flex h-5 w-5 items-center justify-center rounded-full bg-rose-500 text-white text-[10px] font-bold">
              {pendingCount}
            </span>}
          </CardTitle>
        </CardHeader>
        <CardContent className="px-5 pb-4 space-y-3">
          {myVisibleChecks.map((check) => (
            <div
              key={check.id}
              className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3 rounded-xl border border-border/50 bg-card hover:bg-muted/20 transition-all cursor-pointer"
              onClick={() => {
                setDetailCheck(check);
              }}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <Stethoscope className="h-4 w-4 text-rose-500" />
                  <p className="text-sm font-semibold text-foreground truncate">
                    {t("medicalCheckups.title")}
                  </p>
                </div>
                <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1.5">
                  <Clock className="h-3.5 w-3.5 shrink-0 text-muted-foreground/75" />
                  {formatLocalizedDate(check.scheduledDate, i18n.resolvedLanguage || i18n.language)}
                  {check.scheduledTime && ` · ${check.scheduledTime}`}
                </p>
                <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1.5">
                  <MapPin className="h-3.5 w-3.5 shrink-0 text-muted-foreground/75" />
                  {check.institution}
                </p>
                {check.notes && (
                  <p className="text-xs text-muted-foreground/80 mt-1.5 italic line-clamp-1">
                    "{check.notes}"
                  </p>
                )}
              </div>
              {check.status === "pending" ? <div className="flex items-center gap-2 shrink-0 self-end sm:self-center" onClick={(e) => e.stopPropagation()}>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-8 text-xs px-3 border-rose-200 dark:border-rose-950 text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-950/45"
                  onClick={() => {
                    setRejectingCheck(check);
                  }}
                >
                  {t("medicalCheckups.actions.reject")}
                </Button>
                <Button
                  size="sm"
                  className="h-8 text-xs px-3 bg-rose-600 hover:bg-rose-700 text-white"
                  onClick={() => handleAccept(check.id)}
                >
                  {t("medicalCheckups.actions.accept")}
                </Button>
              </div> : <Badge variant="outline" className="shrink-0 self-end border-blue-200 bg-blue-50 text-blue-700 sm:self-center">
                {t("medicalCheckups.assignmentStatus.accepted")}
              </Badge>}
            </div>
          ))}
        </CardContent>
      </Card>}

      {/* Details Dialog */}
      <Dialog open={!!detailCheck || (!!deepLinkAssignmentId && (deepLinkLoading || !!deepLinkError))} onOpenChange={(v) => { if (!v) setDetailCheck(null); }}>
        <DialogContent className="max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-foreground">
              <HeartPulse className="h-5 w-5 text-rose-500" />
              {t("medicalCheckups.detailDialog.title")}
            </DialogTitle>
            {detailCheck && (
              <DialogDescription className="text-xs text-muted-foreground mt-1">
                {t(getMedicalCheckDetailDescriptionKey(detailCheck))}
              </DialogDescription>
            )}
          </DialogHeader>
          {detailCheck && (
            <div className="space-y-4 py-2">
              <div className="rounded-xl border p-3.5 bg-muted/30 space-y-2.5">
                <div className="grid grid-cols-3 gap-1">
                  <span className="text-xs text-muted-foreground">{t("medicalCheckups.table.scheduledDateTime")}</span>
                  <span className="col-span-2 text-xs font-semibold text-foreground">
                    {formatLocalizedDate(detailCheck.scheduledDate, i18n.resolvedLanguage || i18n.language)}
                    {detailCheck.scheduledTime && ` · ${detailCheck.scheduledTime.slice(0, 5)}`}
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-1">
                  <span className="text-xs text-muted-foreground">{t("medicalCheckups.table.institution")}</span>
                  <span className="col-span-2 text-xs font-semibold text-foreground">{detailCheck.institution}</span>
                </div>
                {detailCheck.createdByName && (
                  <div className="grid grid-cols-3 gap-1">
                    <span className="text-xs text-muted-foreground">{t("medicalCheckups.detailDialog.scheduledBy")}</span>
                    <span className="col-span-2 text-xs text-foreground font-medium">{detailCheck.createdByName}</span>
                  </div>
                )}
                <div className="grid grid-cols-3 gap-1">
                  <span className="text-xs text-muted-foreground">{t("medicalCheckups.table.status")}</span>
                  <span className="col-span-2 text-xs font-bold text-rose-500 uppercase tracking-wide">
                    {t(`medicalCheckups.assignmentStatus.${detailCheck.status}`, detailCheck.status)}
                  </span>
                </div>
              </div>

              {detailCheck.notes && (
                <div className="space-y-1">
                  <span className="text-xs font-semibold text-foreground">{t("medicalCheckups.detailDialog.hrNotes")}</span>
                  <div className="rounded-xl border p-3 bg-card text-xs text-muted-foreground italic">
                    "{detailCheck.notes}"
                  </div>
                </div>
              )}

              {detailCheck.rejectionReason && (
                <div className="space-y-1">
                  <span className="text-xs font-semibold text-foreground">{t("medicalCheckups.rejected.reason")}</span>
                  <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-xs text-red-900">{detailCheck.rejectionReason}</div>
                </div>
              )}

              {detailCheck.supportingDocumentId && (
                <button
                  type="button"
                  onClick={() => {
                    void apiDownloadHRDocumentFile(
                      detailCheck.supportingDocumentId!,
                      detailCheck.supportingDocumentName || "document",
                    ).catch((error) => {
                      console.error("[medical check document download] failed", error);
                      toast({
                        title: t("documents.previewError.downloadFailed"),
                        variant: "destructive",
                      });
                    });
                  }}
                  className="flex w-full items-center gap-2 rounded-xl border p-3 text-left text-xs text-primary hover:bg-muted/40"
                >
                  <FileText className="h-4 w-4" />
                  {detailCheck.supportingDocumentName || t("medicalCheckups.detailDialog.supportingDocument")}
                </button>
              )}

              {detailCheck.status === "pending" && (
                <DialogFooter className="flex items-center gap-2 pt-2 sm:justify-end">
                  <Button
                    variant="outline"
                    className="flex-1 sm:flex-initial h-9 text-xs"
                    onClick={() => setRejectingCheck(detailCheck)}
                  >
                    {t("medicalCheckups.actions.reject")}
                  </Button>
                  <Button
                    className="flex-1 sm:flex-initial h-9 text-xs bg-rose-600 hover:bg-rose-700 text-white"
                    onClick={() => handleAccept(detailCheck.id)}
                  >
                    {t("medicalCheckups.actions.accept")}
                  </Button>
                </DialogFooter>
              )}
            </div>
          )}
          {!detailCheck && deepLinkLoading && <p className="py-6 text-center text-sm text-muted-foreground">{t("common.loading")}</p>}
          {!detailCheck && deepLinkError && (
            <p className="py-6 text-center text-sm text-muted-foreground">
              {(deepLinkError as any)?.code === "MEDICAL_CHECK_ACCESS_DENIED"
                ? t("medicalCheckups.detailDialog.inaccessible")
                : t("medicalCheckups.detailDialog.notFound")}
            </p>
          )}
        </DialogContent>
      </Dialog>

      {/* Rejection Dialog */}
      <Dialog open={!!rejectingCheck} onOpenChange={(v) => { if (!v) { setRejectingCheck(null); setRejectionReason(""); setPreferredSlot(""); } }}>
        <DialogContent className="max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-foreground">
              <AlertTriangle className="h-5 w-5 text-red-500" />
              {t("medicalCheckups.rejectDialog.title")}
            </DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground mt-1">
              {t("medicalCheckups.rejectDialog.description")}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="rejection-reason" className="text-xs font-semibold text-foreground">
                {t("medicalCheckups.rejected.reason")} <span className="text-red-500">*</span>
              </Label>
              <Textarea
                id="rejection-reason"
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder={t("medicalCheckups.rejectDialog.placeholder") as string}
                className="min-h-[80px] rounded-xl text-xs"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="preferred-slot" className="text-xs font-semibold text-foreground">
                {t("medicalCheckups.rejectDialog.preferredSlotLabel")}
              </Label>
              <Input
                id="preferred-slot"
                value={preferredSlot}
                onChange={(e) => setPreferredSlot(e.target.value)}
                placeholder={t("medicalCheckups.rejectDialog.preferredSlotPlaceholder") as string}
                className="rounded-xl h-10 text-xs"
              />
            </div>

            <DialogFooter className="flex items-center gap-2 pt-2 sm:justify-end">
              <Button
                variant="outline"
                className="flex-1 sm:flex-initial h-9 text-xs"
                onClick={() => {
                  setRejectingCheck(null);
                  setRejectionReason("");
                  setPreferredSlot("");
                }}
              >
                {t("common.cancel", "Cancel")}
              </Button>
              <Button
                disabled={!rejectionReason.trim()}
                className="flex-1 sm:flex-initial h-9 text-xs bg-red-600 hover:bg-red-700 text-white"
                onClick={handleReject}
              >
                {t("medicalCheckups.actions.confirmReject")}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

// =========================================
// Documents Widget
// =========================================

const STATUS_COLOR: Record<string, string> = {
  Pending:   "bg-amber-500",
  Uploaded:  "bg-blue-500",
  Completed: "bg-green-500",
  Rejected:  "bg-red-500",
};

function DocumentsWidget({ employeeId }: { employeeId?: number }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();
  const isAdminOrHr = user?.role.mainRole === "admin" || user?.role.mainRole === "hr";

  // Personal dashboard widget — always scoped to the current employee's own
  // documents, even for HR/Admin. They use the dedicated Documents page (or
  // HR dashboard) to view everyone else's.
  const { data: docs = [], isLoading } = useQuery<HRDocument[]>({
    queryKey: ["hr-documents-widget", employeeId],
    queryFn: () => apiGetHRDocuments(employeeId),
    staleTime: 60_000,
    enabled: !!employeeId,
  });

  const today = new Date();
  const in60Days = addDays(today, 60);

  const pending = docs.filter((d) => d.status === "Pending");
  const expiring = docs.filter((d) => {
    if (!d.documentExpiry || !EXPIRY_DOCUMENT_TYPES.includes(d.documentType as any)) return false;
    const exp = new Date(d.documentExpiry);
    return exp >= today && exp <= in60Days;
  });
  const recent = docs.slice(0, 5);

  if (!isLoading && docs.length === 0) return null;

  return (
    <Card className="shadow-card">
      <CardHeader className="pb-2 pt-4 px-4">
        <CardTitle className="text-base font-semibold flex items-center gap-2 text-foreground">
          <FileText className="h-4 w-4 text-blue-600" />
          {t("dashboard.documentsWidget.title", "Documents")}
          {pending.length > 0 && (
            <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-amber-500 text-white text-[10px] font-bold">
              {pending.length > 9 ? "9+" : pending.length}
            </span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="px-4 pb-3 space-y-3">
        {expiring.length > 0 && (
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">
              {t("dashboard.documentsWidget.expiringWithin60", "Expiring within 60 days")}
            </p>
            <div className="space-y-1">
              {expiring.slice(0, 3).map((doc) => (
                <div key={doc.id} className="flex items-center gap-2 py-1 border-b last:border-0">
                  <div className="h-1.5 w-1.5 rounded-full bg-amber-500 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm truncate">{doc.documentName}</p>
                    <p className="text-[11px] text-muted-foreground">
                      {isAdminOrHr && doc.employeeName ? `${doc.employeeName} · ` : ""}
                      {t("dashboard.documentsWidget.expires", "Expires")} {format(new Date(doc.documentExpiry!), "MMM d, yyyy")}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        <div className="space-y-1">
          {expiring.length > 0 && recent.length > 0 && (
            <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">
              {t("dashboard.documentsWidget.recent", "Recent")}
            </p>
          )}
          {recent.map((doc) => (
            <div key={doc.id} className="flex items-center gap-2 py-1 border-b last:border-0">
              <div className={`h-1.5 w-1.5 rounded-full shrink-0 ${STATUS_COLOR[doc.status] ?? "bg-muted-foreground"}`} />
              <div className="flex-1 min-w-0">
                <p className="text-sm truncate">{doc.documentName}</p>
                {isAdminOrHr && doc.employeeName && (
                  <p className="text-[11px] text-muted-foreground">{doc.employeeName}</p>
                )}
              </div>
              <span className="text-[10px] text-muted-foreground shrink-0">{doc.status}</span>
            </div>
          ))}
        </div>
        <button
          onClick={() => navigate("/my-documents")}
          className="w-full flex items-center justify-center gap-1 text-sm text-blue-600 hover:text-blue-700 font-medium pt-1 transition-colors"
        >
          {t("dashboard.documentsWidget.viewAll", "View all documents")} <ChevronRight className="h-4 w-4" />
        </button>
      </CardContent>
    </Card>
  );
}

// =========================================
// My Tasks Widget
// =========================================

function MyTasksWidget() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { data: tasks = [] } = useQuery<WorkflowTask[]>({
    queryKey: ["my-workflow-tasks"],
    queryFn: apiGetMyWorkflowTasks,
    staleTime: 60_000,
  });
  const pending = tasks.filter((t) => t.status === "pending" && !t.is_archived);
  if (pending.length === 0) return null;
  return (
    <Card className="shadow-card">
      <CardHeader className="pb-2 pt-4 px-4">
        <CardTitle className="text-base font-semibold flex items-center gap-2 text-foreground">
          <ClipboardList className="h-4 w-4 text-primary" />
          {t("dashboard.myTasksWidget.title", "My Tasks")}
          <span className="ml-1 inline-flex h-5 w-5 items-center justify-center rounded-full bg-primary text-primary-foreground text-[10px] font-bold">
            {pending.length > 9 ? "9+" : pending.length}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="px-4 pb-3 space-y-1">
        {pending.slice(0, 3).map((task) => (
          <div key={task.id} className="flex items-center gap-2 py-1.5 border-b last:border-0">
            <div className="h-1.5 w-1.5 rounded-full bg-primary shrink-0" />
            <p className="text-sm flex-1 truncate">{task.title}</p>
            {task.due_date && (
              <span className="text-[11px] text-muted-foreground shrink-0">
                {format(new Date(task.due_date), "MMM d")}
              </span>
            )}
          </div>
        ))}
        <button
          onClick={() => navigate("/my-tasks")}
          className="w-full flex items-center justify-center gap-1 text-sm text-primary hover:text-primary/80 font-medium pt-1 transition-colors"
        >
          {t("dashboard.myTasksWidget.viewAll", "View all tasks")} <ChevronRight className="h-4 w-4" />
        </button>
      </CardContent>
    </Card>
  );
}

// =========================================
// Pending Surveys Widget
// =========================================

function PendingSurveysWidget() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { data: surveys = [] } = useQuery<Survey[]>({
    queryKey: ["surveys-dashboard"],
    queryFn: apiGetEmployeeSurveys,
    staleTime: 60_000,
  });
  const active = surveys.filter((s) => s.status === "active" && !s.hasResponded);
  if (active.length === 0) return null;
  return (
    <Card className="shadow-card">
      <CardHeader className="pb-2 pt-4 px-4">
        <CardTitle className="text-base font-semibold flex items-center gap-2 text-foreground">
          <FileText className="h-4 w-4 text-violet-600" />
          {t("dashboard.surveysWidget.title", "Pending Surveys")}
          <span className="ml-1 inline-flex h-5 w-5 items-center justify-center rounded-full bg-violet-500 text-white text-[10px] font-bold">
            {active.length > 9 ? "9+" : active.length}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="px-4 pb-3 space-y-1">
        {active.slice(0, 3).map((survey) => (
          <button
            key={survey.id}
            onClick={() => navigate(`/my-surveys?survey=${survey.id}`)}
            className="w-full flex items-center gap-2 py-1.5 border-b last:border-0 hover:bg-muted/40 -mx-1 px-1 rounded transition-colors text-left"
          >
            <div className="h-1.5 w-1.5 rounded-full bg-violet-500 shrink-0" />
            <p className="text-sm flex-1 truncate">{survey.title}</p>
            <ChevronRight className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
          </button>
        ))}
        <button
          onClick={() => navigate("/my-surveys")}
          className="w-full flex items-center justify-center gap-1 text-sm text-violet-600 hover:text-violet-700 font-medium pt-1 transition-colors"
        >
          {t("dashboard.surveysWidget.viewAll", "View surveys")} <ChevronRight className="h-4 w-4" />
        </button>
      </CardContent>
    </Card>
  );
}

// =========================================
// Daily Briefing Bar
// =========================================

function DailyBriefingBar({ leaveRequests }: { leaveRequests: LeaveRequest[] }) {
  const { t } = useTranslation();
  const { data: tasks = [] } = useQuery<WorkflowTask[]>({
    queryKey: ["my-workflow-tasks"],
    queryFn: apiGetMyWorkflowTasks,
    staleTime: 60_000,
  });
  const { data: surveys = [] } = useQuery<Survey[]>({
    queryKey: ["surveys-dashboard"],
    queryFn: apiGetEmployeeSurveys,
    staleTime: 60_000,
  });

  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  const yesterdayStr = yesterday.toDateString();

  const doneTasks = tasks.filter(
    (t) => t.status === "completed" && t.completed_at && new Date(t.completed_at).toDateString() === yesterdayStr
  );
  const pendingTasks  = tasks.filter((t) => t.status === "pending" && !t.is_archived);
  const openSurveys   = surveys.filter((s) => s.status === "active");
  const pendingLeave  = leaveRequests.filter((r) => r.status === "Pending");

  const todayItems: { label: string; dot: string }[] = [
    ...(pendingTasks.length  > 0 ? [{ label: t("dashboard.dailyBriefing.tasksPending", { count: pendingTasks.length }), dot: "bg-primary"    }] : []),
    ...(pendingLeave.length  > 0 ? [{ label: t("dashboard.dailyBriefing.leavePending", { count: pendingLeave.length }), dot: "bg-amber-500"  }] : []),
    ...(openSurveys.length   > 0 ? [{ label: t("dashboard.dailyBriefing.surveysOpen", { count: openSurveys.length }),  dot: "bg-violet-500" }] : []),
  ];

  if (doneTasks.length === 0 && todayItems.length === 0) {
    return (
      <p className="text-xs text-muted-foreground/70 mt-2">
        {t("dashboard.dailyBriefing.allCaughtUp", "All caught up - nothing needs your attention today")}
      </p>
    );
  }

  return (
    <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground flex-wrap mt-2">
      {doneTasks.length > 0 && (
        <>
          <span className="text-muted-foreground/60">{t("dashboard.dailyBriefing.yesterday", "Yesterday")}:</span>
          <span className="flex items-center gap-1 text-emerald-600 dark:text-emerald-400">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 shrink-0" />
            {t("dashboard.dailyBriefing.tasksDone", { count: doneTasks.length })}
          </span>
          {todayItems.length > 0 && <span className="opacity-30">·</span>}
        </>
      )}
      {todayItems.length > 0 && (
        <>
          {doneTasks.length > 0 && <span className="text-muted-foreground/60">{t("dashboard.dailyBriefing.today", "Today")}:</span>}
          {todayItems.map((item, i) => (
            <span key={i} className="flex items-center gap-2">
              {i > 0 && <span className="opacity-30">·</span>}
              <span className="flex items-center gap-1">
                <span className={`h-1.5 w-1.5 rounded-full ${item.dot} shrink-0`} />
                {item.label}
              </span>
            </span>
          ))}
        </>
      )}
    </div>
  );
}

// =========================================
// Dashboard Component
// =========================================

const DEFAULT_MAIN_ORDER    = ["inbox", "calendar", "celebrations"];
const DEFAULT_SIDEBAR_ORDER = ["profile_card", "time_off", "team", "documents", "my_tasks", "surveys", "quick_links", "raise_concern"];

function parseWidgetOrder(savedJson: string | undefined, defaults: string[]): string[] {
  try {
    const saved: string[] = JSON.parse(savedJson ?? "[]");
    if (!Array.isArray(saved) || saved.length === 0) return defaults;
    const valid = saved.filter((id) => defaults.includes(id));
    const missing = defaults.filter((id) => !saved.includes(id));
    return [...valid, ...missing];
  } catch {
    return defaults;
  }
}

const Dashboard = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const mainRole = user?.role.mainRole ?? "user";
  const isAdminOrHr = mainRole === "admin" || mainRole === "hr";

  const { data: employeeProfile } = useQuery({
    queryKey: ["employee-by-email", user?.email],
    queryFn: () => apiGetEmployeeByEmail(user!.email),
    enabled: !!user?.email,
  });

  const numericEmpId = user?.employeeId ? Number(user.employeeId) : undefined;

  const { data: widgetPrefs = {}, isLoading: prefsLoading } = useQuery({
    queryKey: ["ui-prefs", getTenantSlug()],
    queryFn: apiGetUiPrefs,
    staleTime: Infinity,
  });

  // Signal the splash screen to close once widget prefs have resolved
  useEffect(() => {
    if (!prefsLoading) {
      document.dispatchEvent(new Event("app-ready"));
    }
  }, [prefsLoading]);

  const { canAccess } = useModuleAccess();
  const wVisible = (id: string) => {
    const owner: Record<string, string> = { profile_card: "myProfile", time_off: "leaveManagement", documents: "documents", my_tasks: "myTasks", surveys: "mySurveys", calendar: "leaveManagement" };
    if (owner[id] && !canAccess(owner[id])) return false;
    const k = `widget_${id}_visible`;
    return !(k in widgetPrefs) || widgetPrefs[k] === "true";
  };

  const mainOrder    = parseWidgetOrder(widgetPrefs["widget_order_main"],    DEFAULT_MAIN_ORDER);
  const sidebarOrder = parseWidgetOrder(widgetPrefs["widget_order_sidebar"], DEFAULT_SIDEBAR_ORDER);

  const { data: myLeaveRequests = [] } = useQuery({
    queryKey: ["leaveRequests", "my", numericEmpId],
    queryFn: () => apiGetLeaveRequests({ employeeId: numericEmpId }),
    enabled: !!numericEmpId,
  });

  const pendingCount = myLeaveRequests.filter((r) => r.status === "Pending").length;
  const firstName = user?.name?.split(" ")[0] ?? "";

  const renderMainWidget = (id: string) => {
    if (!wVisible(id)) return null;
    switch (id) {
      case "inbox":            return <InboxHighlights key={id} />;
      case "calendar":         return <ThisWeekCalendar key={id} employeeProfile={employeeProfile} />;
      case "pending_approvals": return null;
      case "celebrations":     return <PeopleToCelebrate key={id} />;
      default: return null;
    }
  };

  const renderSidebarWidget = (id: string) => {
    if (!wVisible(id)) return null;
    switch (id) {
      case "profile_card": return employeeProfile ? <ProfileCard key={id} employee={employeeProfile} /> : null;
      case "time_off":     return (employeeProfile && user?.employeeId)
        ? <TimeOffWidget key={id} employeeId={employeeProfile.id} leaveRequests={myLeaveRequests} />
        : null;
      case "team":         return <TeamWidget key={id} selfServiceOnly />;
      case "documents":    return <DocumentsWidget key={id} employeeId={numericEmpId} />;
      case "my_tasks":     return <MyTasksWidget key={id} />;
      case "surveys":      return <PendingSurveysWidget key={id} />;
      case "open_cases":   return null;
      case "quick_links":  return <ShortcutsWidget key={id} />;
      case "raise_concern": return null;
      default: return null;
    }
  };

  return (
    <AppLayout>
      <div className="w-full">
        {/* Greeting Header */}
        <div className="flex items-start justify-between mb-6 gap-3 flex-wrap">
          {/* Left spacer — mirrors toggle width to keep greeting visually centred */}
          <div className="flex-1 hidden sm:block" />

          <div className="text-center flex-1 sm:flex-none">
            <h1 className="text-3xl font-bold text-foreground">
              {t(getGreetingKey())}, {firstName}
            </h1>
            {(() => { const q = getQuoteOfTheDay(); return (
              <p className="text-xs text-muted-foreground/70 mt-2 italic max-w-md mx-auto">
                "{q.text}" <span className="not-italic">— {q.author}</span>
              </p>
            ); })()}
          </div>

          {/* Dashboard toggle — same position and style as HR Dashboard */}
          <div className="flex-1 flex justify-end">
            {isAdminOrHr && (
              <div data-tour="dashboard-toggle" className="flex items-center gap-0.5 p-0.5 rounded-lg bg-muted border border-border">
                <span className="flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold bg-background shadow-sm text-foreground">
                  <LayoutGrid className="h-3.5 w-3.5" />
                  {t("dashboard.employeeDashboard", "Employee Dashboard")}
                </span>
                <button
                  onClick={() => {
                    try { localStorage.setItem("hr_dashboard_view", "hr"); } catch {}
                    navigate("/hr-dashboard");
                  }}
                  className="flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-background/60 transition-colors"
                >
                  <BarChart3 className="h-3.5 w-3.5" />
                  {t("nav.hrDashboard", "HR Dashboard")}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Main two-column layout */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-5">
          {prefsLoading ? (
            <>
              {/* Left skeleton */}
              <div className="space-y-4">
                <div className="h-28 rounded-xl bg-muted animate-pulse" />
                <div className="h-48 rounded-xl bg-muted animate-pulse" />
                <div className="h-36 rounded-xl bg-muted animate-pulse" />
              </div>
              {/* Right skeleton */}
              <div className="space-y-4">
                <div className="h-52 rounded-xl bg-muted animate-pulse" />
                <div className="h-32 rounded-xl bg-muted animate-pulse" />
                <div className="h-40 rounded-xl bg-muted animate-pulse" />
              </div>
            </>
          ) : (
            <>
              {/* Left Column */}
              <div className="space-y-4">
                <div data-tour="widget-medical_checks">
                  <MedicalChecksWidget employeeProfile={employeeProfile} showCard={false} />
                </div>
                {mainOrder.map((id) => {
                  const el = renderMainWidget(id);
                  return el ? <div key={id} data-tour={`widget-${id}`}>{el}</div> : null;
                })}
              </div>

              {/* Right Sidebar */}
              <div className="space-y-4">
                {sidebarOrder.map((id) => {
                  const el = renderSidebarWidget(id);
                  return el ? <div key={id} data-tour={`widget-${id}`}>{el}</div> : null;
                })}
              </div>
            </>
          )}
        </div>
      </div>
    </AppLayout>
  );
};

export default Dashboard;
